import type { useCreateHardEligibilitySession } from "@usebridge/sdk-react"

type Session = ReturnType<ReturnType<typeof useCreateHardEligibilitySession>>
type Result = Session["state"]
export type InsuranceMember = { firstName: string; lastName: string; dateOfBirth: string }
export type InsuranceInputs = {
  state: string
  payerId: string
  payerName: string
  memberId: string
}
export type InsuranceCheckInputs = InsuranceInputs & { serviceTypeIds: string[]; member: InsuranceMember }
export type InsuranceResult = Pick<
  Result,
  "providers" | "patientResponsibility" | "serviceEligibility" | "ineligibilityReason"
> & {
  status: "ELIGIBLE" | "INELIGIBLE"
  policyId?: string
}
export type InsuranceError = { code: string; retryable: boolean }
export type InsuranceEvent =
  | { type: "INSURANCE_STARTED"; requestId: string; inputs: InsuranceCheckInputs }
  | { type: "INSURANCE_RESULT"; requestId: string; result: InsuranceResult }
  | { type: "INSURANCE_FAILED"; requestId: string; error: InsuranceError }
  | { type: "INSURANCE_INVALIDATED" }

export function insuranceResultStep(result: InsuranceResult): "covered" | "notCovered" | "noProviders" {
  if (result.status === "ELIGIBLE") return result.providers?.length ? "covered" : "noProviders"
  return result.ineligibilityReason?.code === "PROVIDERS" || result.ineligibilityReason?.code === "OUT_OF_NETWORK"
    ? "noProviders"
    : "notCovered"
}

// Keep malformed Bridge mappings from rejecting valid clinicians in the same availability request.
const CLINICIAN_ID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i

// undefined leaves self-pay discovery unrestricted; [] must remain an explicit restriction.
export function insuranceClinicianIds(result: InsuranceResult | null): string[] | undefined {
  if (!result || insuranceResultStep(result) !== "covered") return undefined
  return [
    ...new Set(
      (result.providers ?? []).flatMap(({ externalId }) =>
        externalId && CLINICIAN_ID_PATTERN.test(externalId) ? [externalId.toLowerCase()] : []
      )
    )
  ].sort()
}
